/**
 * React Query hooks for remote reports.
 * Distinct from in-person report navigation (useNavigateToReport).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as service from '@/services/remote/reports.service';
import { shouldRetryUnitGet } from '@/hooks/useUnitGetNotFound';

/** React Query keys for remote reports (`/comptes-rendus`). */
export const reportKeys = {
  all: ['reports-remote'] as const,
  list: (params?: object) => [...reportKeys.all, 'list', params] as const,
  detail: (id: string) => [...reportKeys.all, 'detail', id] as const,
  bySession: (sessionId: string) => [...reportKeys.all, 'by-session', sessionId] as const,
};

/** `GET /comptes-rendus/:id` */
export const useGetReport = (id: string, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: reportKeys.detail(id),
    queryFn: () => service.getReport(id),
    enabled: !!id && (options?.enabled ?? true),
    retry: shouldRetryUnitGet,
  });

/** `GET /comptes-rendus` filtered by session id. */
export const useGetReportBySession = (sessionId: string, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: reportKeys.bySession(sessionId),
    queryFn: () => service.getReportBySession(sessionId),
    enabled: !!sessionId && (options?.enabled ?? true),
  });

/** `PATCH /comptes-rendus/:id` — invalidates list + detail. */
export const usePatchReport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: Record<string, unknown> }) =>
      service.patchReport(id, fields),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: reportKeys.list() });
      void qc.invalidateQueries({ queryKey: reportKeys.detail(id) });
    },
  });
};
